import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MagnifyingGlassIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const GlobalSearch = ({ compact = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchMode, setSearchMode] = useState('both');
  const [expanded, setExpanded] = useState(false);
  
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  const handleResultClick = useCallback((result) => {
    // Handle ETF vs Stock navigation
    if (result.type === 'etf') {
      navigate(`/etf/${result.ticker}`, { 
        state: { ticker: result.ticker }
      });
    } else {
      // Create a security object similar to what FullPowerListPage passes
      const security = {
        security_id: result.security_id,
        ticker: result.ticker,
        name: result.name,
        sector: result.sector,
        rank_global: result.rank_global,
        signal_weight: result.signal_weight,
        // Add any other fields that might be needed
        is_masked: false // Since search results are not masked
      };
      
      navigate(`/ticker/${result.security_id}`, { 
        state: { security }
      });
    }
    
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
        if (compact) setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [compact]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen || results.length === 0) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prev => 
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
          break;
        case 'Enter':
          event.preventDefault();
          if (selectedIndex >= 0 && results[selectedIndex]) {
            handleResultClick(results[selectedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, handleResultClick]);

  // Search function - now searches both stocks and ETFs
  const searchCompanies = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    console.log('Searching for:', searchQuery, 'Mode:', searchMode); // Debug log
    
    try {
      const token = localStorage.getItem('access_token');
      
      // Search both stocks and ETFs in parallel
      const [stockResponse, etfResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/ea_stock_rankings/search?q=${encodeURIComponent(searchQuery)}&mode=${searchMode}`, {
          headers: {
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${process.env.REACT_APP_API_URL}/etf/search?q=${encodeURIComponent(searchQuery)}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }).catch(err => {
          console.log('ETF search error (non-critical):', err);
          return { ok: false }; // Return a dummy failed response
        })
      ]);

      let allResults = [];
      
      // Add stock results
      if (stockResponse.ok) {
        const stockData = await stockResponse.json();
        console.log('Stock search results:', stockData);
        allResults = [...(stockData.results || [])];
      }
      
      // Add ETF results
      if (etfResponse.ok) {
        const etfData = await etfResponse.json();
        console.log('ETF search results:', etfData);
        // ETF results already have type: 'etf' from backend
        allResults = [...allResults, ...(etfData || [])];
      }
      
      console.log('Combined results:', allResults);
      setResults(allResults);
      setIsOpen(true);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [searchMode]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchCompanies(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, searchMode, searchCompanies]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setSelectedIndex(-1);
  };

  const highlightMatch = (text, query) => {
    if (!query || query.length < 2) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : part
    );
  };

  const handleExpand = () => {
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const searchInput = (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <MagnifyingGlassIcon className="h-4 w-4 text-text-300" />
      </div>
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => query.length >= 2 && setIsOpen(true)}
        placeholder={
          searchMode === 'companies' ? 'Search companies...' :
          searchMode === 'tickers' ? 'Search tickers...' :
          'Search stocks & ETFs...'
        }
        className="block w-full pl-9 pr-3 py-1.5 border border-bg-300 rounded-lg leading-5 bg-bg-200 placeholder-text-300 text-text-100 focus:outline-none focus:ring-1 focus:ring-primary-100 focus:border-primary-100 text-sm"
      />
      {isLoading && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-100"></div>
        </div>
      )}
    </div>
  );

  const dropdown = isOpen && (
    <div className={`${compact ? 'relative mx-0' : 'absolute w-full'} z-50 mt-1 bg-white shadow-lg max-h-80 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm`}>
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="flex rounded-md shadow-sm text-xs">
          {[
            { value: 'companies', label: 'Companies' },
            { value: 'tickers', label: 'Tickers' },
            { value: 'both', label: 'Both' },
          ].map((opt, i) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSearchMode(opt.value)}
              className={`relative inline-flex items-center px-2 py-1 border text-xs font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-100 ${
                i === 0 ? 'rounded-l-md' : i === 2 ? 'rounded-r-md -ml-px' : '-ml-px'
              } ${
                searchMode === opt.value
                  ? 'bg-primary-100 border-primary-100 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {results.length === 0 && !isLoading && query.length >= 2 && (
        <div className="px-4 py-3 text-gray-500 text-center text-sm">
          No results found for "{query}"
        </div>
      )}
      
      {results.map((result, index) => (
        <div
          key={result.type === 'etf' ? `etf-${result.ticker}` : `stock-${result.security_id}`}
          ref={index === selectedIndex ? resultsRef : null}
          className={`cursor-pointer px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
            index === selectedIndex ? 'bg-gray-50' : ''
          }`}
          onClick={() => handleResultClick(result)}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900 text-sm">
                      {highlightMatch(result.ticker, query)}
                    </span>
                    {result.type === 'etf' ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        ETF
                      </span>
                    ) : (
                      <>
                        <span className="text-xs text-gray-500">
                          #{result.rank_global}
                        </span>
                        {searchMode === 'both' && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                            result.ticker.toLowerCase().includes(query.toLowerCase())
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {result.ticker.toLowerCase().includes(query.toLowerCase()) ? 'Ticker' : 'Company'}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div className="text-sm text-gray-600 truncate">
                    {highlightMatch(result.name, query)}
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    {result.sector && (
                      <span className="text-xs text-gray-500">
                        {result.sector}
                      </span>
                    )}
                    {(result.signal_weight || result.signal) && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Score: {Math.round(result.signal_weight || result.signal)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
          </div>
        </div>
      ))}
      
      {results.length > 0 && (
        <div className="px-4 py-2 text-xs text-gray-500 bg-gray-50 border-t">
          ↑↓ navigate · Enter select · Esc close
        </div>
      )}
    </div>
  );

  if (compact) {
    return (
      <>
        <button
          onClick={handleExpand}
          className="flex items-center justify-center p-2 rounded-lg text-text-300 hover:text-text-100 hover:bg-bg-200 transition-colors"
          title="Search"
        >
          <MagnifyingGlassIcon className="h-5 w-5" />
        </button>

        {expanded && (
          <div className="fixed inset-0 z-50" ref={searchRef}>
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => { setExpanded(false); setIsOpen(false); }}
            />
            {/* Search panel */}
            <div className="relative mx-4 mt-3">
              <div className="bg-bg-200 rounded-xl border border-bg-300 p-3 shadow-2xl">
                {searchInput}
              </div>
              {dropdown}
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div ref={searchRef} className="relative w-full max-w-md">
      {searchInput}
      {dropdown}
    </div>
  );
};

export default GlobalSearch; 